import {
  decodePeriodValue,
  formatPeriodTriggerLabel,
  fromLumenPeriodValue,
  getMonthNames,
  toLumenPeriodValue,
} from '@components'

import { type PeriodValue as LumenPeriodValue, PeriodPicker, type PeriodPickerMode } from '@shinetools/lumen-react'
import { type MouseEvent, ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useLocale } from '../../../../hooks/useLocale'
import { useOrganizationFiscalYears } from '../../../../hooks/useOrganizationFiscalYears'
import type { PeriodValue } from '../../../../types/periodValue'
import { isFiniteReportPeriod } from '../../utils/isFiniteReportPeriod'

// "All time" (via `allTimeLabel`) is not part of this design - excluding `week` and omitting a dedicated "all"
// entry (Lumen has none; it's a shortcut inside the `custom` panel, see FEF-852) leaves our four supported modes.
// Spelled out as literals rather than `PeriodPickerMode.month` etc: this is a module-scope constant, and some
// __legacy_tests__ files replace the `@shinetools/lumen-react` mock with an incomplete local factory that only
// covers what they render; a real property lookup here would throw for any consumer that imports this module
// transitively (even without rendering it) under one of those incomplete mocks.
const AVAILABLE_MODES: readonly PeriodPickerMode[] = ['month', 'quarter', 'fiscalYear', 'custom']

interface ExpertReportPeriodPickerProps {
  encodedPeriod: string
  onChange: (value: PeriodValue) => void
  onOpen?: () => void
}

/**
 * Shared Lumen `PeriodPicker` for expert reports (supplier/customer balance, P&L, trial balance): the four fixed
 * modes (Month, Quarter, Fiscal Year, Custom Range), bounded to the organization's fiscal-year history, with the
 * open-ended `All` and incomplete custom ranges filtered out before `onChange` fires. Business side effects
 * (analytics, other URL params, syncing other state) belong in the consumer, not here.
 */
export const ExpertReportPeriodPicker = ({
  encodedPeriod,
  onChange,
  onOpen,
}: ExpertReportPeriodPickerProps): ReactElement => {
  const { t } = useTranslation()
  const locale = useLocale()
  const { maxDate, minDate } = useOrganizationFiscalYears()

  const value = useMemo(() => toLumenPeriodValue(decodePeriodValue(encodedPeriod)), [encodedPeriod])
  const monthLabels = useMemo(() => getMonthNames(locale.code), [locale.code])

  // Lumen's PeriodPicker spreads `getReferenceProps({ onClick })` after rest props, which overwrites a passed-in
  // `onClick`. `onMouseDown` survives that merge; at mousedown time `aria-expanded` is still the pre-toggle value, so
  // we can tell an open apart from a close.
  const handleTriggerMouseDown = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (event.currentTarget.getAttribute('aria-expanded') !== 'false') {
        return
      }

      onOpen?.()
    },
    [onOpen],
  )

  const handleChange = useCallback(
    (lumenValue: LumenPeriodValue | null): void => {
      const periodValue = fromLumenPeriodValue(lumenValue)

      if (!isFiniteReportPeriod(periodValue)) {
        return
      }

      onChange(periodValue)
    },
    [onChange],
  )

  const handleFormatTriggerLabel = useCallback(
    (lumenValue: LumenPeriodValue) => formatPeriodTriggerLabel({ locale, monthLabels, t, value: lumenValue }),
    [locale, monthLabels, t],
  )

  return (
    <PeriodPicker
      allTimeLabel={t('period_select.mode.all')}
      availableModes={AVAILABLE_MODES}
      formatTriggerLabel={handleFormatTriggerLabel}
      labels={{
        customMode: t('expert_reports.period_select.mode.custom'),
        customRangeTitle: t('period_select.mode.custom_date'),
        endDatePlaceholder: t('period_select.custom_date.date_placeholder'),
        fiscalYearMode: t('expert_reports.period_select.mode.fiscal_year'),
        monthMode: t('period_select.mode.month'),
        quarterMode: t('period_select.mode.quarter'),
        startDatePlaceholder: t('period_select.custom_date.date_placeholder'),
      }}
      maxDate={maxDate}
      minDate={minDate}
      monthLabels={monthLabels}
      onChange={handleChange}
      onMouseDown={handleTriggerMouseDown}
      placeholder={t('period_select.trigger_button_placeholder')}
      saveLabel={t('period_select.save_button')}
      shape="rounded"
      value={value}
    />
  )
}
